import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { cacheGet, cacheSet, cacheInvalidate } from '../services/apiCache';
import InstrumentDetail from '../components/Instruments/InstrumentDetail';
import ImportModal from '../components/Instruments/ImportModal';

async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

const STATUS_CHIPS = [
  { label: 'Todos', value: '' },
  { label: 'Calibrado', value: 'calibrado' },
  { label: 'Não calibrado', value: 'nao_calibrado' },
  { label: 'Aguardando laudo', value: 'aguardando_laudo' },
  { label: 'Em calibração', value: 'em_calibracao' },
  { label: 'A vencer', value: 'a_vencer' },
  { label: 'Vencido', value: 'vencido' },
  { label: 'Bloqueado', value: 'bloqueado' },
];

const CHIP_COLORS = {
  calibrado: 'bg-green-900/60 text-green-300 border-green-700/60',
  nao_calibrado: 'bg-gray-700/60 text-gray-300 border-gray-600/60',
  aguardando_laudo: 'bg-orange-900/60 text-orange-300 border-orange-700/60',
  em_calibracao: 'bg-purple-900/60 text-purple-300 border-purple-700/60',
  a_vencer: 'bg-yellow-900/60 text-yellow-300 border-yellow-700/60',
  vencido: 'bg-red-900/60 text-red-300 border-red-700/60',
  bloqueado: 'bg-slate-700/60 text-slate-300 border-slate-600/60',
  '': 'bg-[var(--surface-4)] text-slate-300 border-[var(--surface-5)]',
};

export default function InstrumentsPage() {
  const [instruments, setInstruments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [searchDesc, setSearchDesc] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCriticidade, setFilterCriticidade] = useState('');
  const [filterVencimento, setFilterVencimento] = useState('');
  const [filterVencimentoDate, setFilterVencimentoDate] = useState('');
  const [sortBy, setSortBy] = useState('tag_asc');

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const statusParam = searchParams.get('status');
    const sectorParam = searchParams.get('sector');
    if (statusParam) setFilterStatus(statusParam);
    if (sectorParam) setFilterSector(sectorParam);
    if (statusParam || sectorParam) setSearchParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInstruments = useCallback(async (skipCache = false) => {
    const key = '/instruments?calibratable=true';
    if (!skipCache) {
      const cached = cacheGet(key);
      if (cached) { setInstruments(cached); setLoading(false); return; }
    }
    try {
      const res = await api.get(key);
      const data = res.data.dados || res.data;
      cacheSet(key, data, 5 * 60_000);
      setInstruments(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInstruments(); }, [loadInstruments]);

  const getDaysRemaining = (nextDate) => {
    if (!nextDate) return null;
    return Math.ceil((new Date(nextDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const statusLabel = (s) => {
    const map = {
      aprovado: 'Aprovado', calibrado: 'Calibrado', a_vencer: 'A vencer',
      vencido: 'Vencido', bloqueado: 'Bloqueado', em_calibracao: 'Em calibração',
      aguardando_laudo: 'Aguardando laudo', nao_calibrado: 'Não calibrado',
    };
    return map[s] || s;
  };

  const statusClass = (s) => {
    const map = {
      aprovado: 'bg-green-900 text-green-300', calibrado: 'bg-green-900 text-green-300',
      a_vencer: 'bg-yellow-900 text-yellow-300', vencido: 'bg-red-900 text-red-300',
      bloqueado: 'bg-slate-700 text-slate-300', em_calibracao: 'bg-purple-900 text-purple-300',
      aguardando_laudo: 'bg-orange-900 text-orange-300', nao_calibrado: 'bg-gray-700 text-gray-400',
    };
    return map[s] || 'bg-gray-700 text-gray-400';
  };

  const getFiltered = () => {
    let list = [...instruments];

    if (searchDesc) list = list.filter(i => i.description?.toLowerCase().includes(searchDesc.toLowerCase()) || i.tag.toLowerCase().includes(searchDesc.toLowerCase()));
    if (filterSector) list = list.filter(i => i.sector === filterSector);
    if (filterStatus) list = list.filter(i => i.calibrationStatus === filterStatus);
    if (filterCriticidade) list = list.filter(i => i.criticity === filterCriticidade);

    if (filterVencimento && filterVencimento !== 'custom') {
      const days = parseInt(filterVencimento);
      list = list.filter(i => {
        const d = getDaysRemaining(i.nextCalibrationDate);
        return d !== null && d <= days;
      });
    }
    if (filterVencimento === 'custom' && filterVencimentoDate) {
      const cutoff = new Date(filterVencimentoDate);
      list = list.filter(i => i.nextCalibrationDate && new Date(i.nextCalibrationDate) <= cutoff);
    }

    list.sort((a, b) => {
      if (sortBy === 'tag_asc') return a.tag.localeCompare(b.tag);
      if (sortBy === 'tag_desc') return b.tag.localeCompare(a.tag);
      if (sortBy === 'vencimento_asc') {
        const da = getDaysRemaining(a.nextCalibrationDate) ?? 99999;
        const db = getDaysRemaining(b.nextCalibrationDate) ?? 99999;
        return da - db;
      }
      if (sortBy === 'vencimento_desc') {
        const da = getDaysRemaining(a.nextCalibrationDate) ?? -99999;
        const db = getDaysRemaining(b.nextCalibrationDate) ?? -99999;
        return db - da;
      }
      return 0;
    });

    return list;
  };

  const filtered = getFiltered();
  const sectors = [...new Set(instruments.map(i => i.sector).filter(Boolean))];
  const hasActiveFilters = filterStatus || filterCriticidade || filterVencimento || filterSector || searchDesc;

  if (loading) return <div className="p-4 text-slate-400">Carregando...</div>;

  return (
    <div className="flex h-full">

      {/* ── Painel esquerdo — lista ─────────────────────────────────────────── */}
      {/* Mobile: hidden when detail is open; Desktop: fixed w-80 */}
      <div className={`${selected ? 'hidden lg:flex' : 'flex'} w-full lg:w-80 flex-col bg-[var(--surface-3)] border-r border-[var(--surface-4)] overflow-hidden`}>
        <div className="p-3 border-b border-[var(--surface-4)] space-y-2 overflow-y-auto">

          {/* Busca */}
          <input
            type="text"
            placeholder="Buscar por TAG ou descrição..."
            value={searchDesc}
            onChange={e => setSearchDesc(e.target.value)}
            className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-md px-3 py-2 text-sm text-[var(--text-base)] placeholder-slate-600 outline-none focus:border-blue-600"
          />

          {/* Status chips */}
          <div className="flex flex-wrap gap-1">
            {STATUS_CHIPS.map(chip => (
              <button
                key={chip.value}
                onClick={() => setFilterStatus(chip.value === filterStatus ? '' : chip.value)}
                className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                  filterStatus === chip.value
                    ? (CHIP_COLORS[chip.value] || CHIP_COLORS['']) + ' opacity-100'
                    : 'bg-transparent text-slate-500 border-[var(--surface-4)] hover:border-slate-500'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {/* Setor + Criticidade */}
          <div className="grid grid-cols-2 gap-1.5">
            <select
              value={filterSector}
              onChange={e => setFilterSector(e.target.value)}
              className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-md px-2 py-1.5 text-xs text-[var(--text-base)] outline-none"
            >
              <option value="">Todos setores</option>
              {sectors.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={filterCriticidade}
              onChange={e => setFilterCriticidade(e.target.value)}
              className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-md px-2 py-1.5 text-xs text-[var(--text-base)] outline-none"
            >
              <option value="">Criticidade</option>
              <option value="high">Alta</option>
              <option value="medium">Média</option>
              <option value="low">Baixa</option>
            </select>
          </div>

          {/* Vencimento + Ordenação */}
          <div className="grid grid-cols-2 gap-1.5">
            <select
              value={filterVencimento}
              onChange={e => setFilterVencimento(e.target.value)}
              className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-md px-2 py-1.5 text-xs text-[var(--text-base)] outline-none"
            >
              <option value="">Vencimento</option>
              <option value="30">≤ 30 dias</option>
              <option value="60">≤ 60 dias</option>
              <option value="90">≤ 90 dias</option>
              <option value="custom">Data exata</option>
            </select>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-md px-2 py-1.5 text-xs text-[var(--text-base)] outline-none"
            >
              <option value="tag_asc">TAG A-Z</option>
              <option value="tag_desc">TAG Z-A</option>
              <option value="vencimento_asc">Vencimento ↑</option>
              <option value="vencimento_desc">Vencimento ↓</option>
            </select>
          </div>

          {filterVencimento === 'custom' && (
            <input
              type="date"
              value={filterVencimentoDate}
              onChange={e => setFilterVencimentoDate(e.target.value)}
              className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-md px-2 py-1.5 text-xs text-[var(--text-base)] outline-none"
            />
          )}

          {/* Exportar / Importar / Template */}
          <div className="flex gap-1.5">
            <button
              onClick={() => downloadBlob('/instruments/export', `instrumentos_${new Date().toISOString().slice(0,10)}.xlsx`)}
              className="flex-1 text-[11px] py-1.5 rounded-md bg-emerald-900/40 text-emerald-400 border border-emerald-800/50 hover:bg-emerald-900/70 transition-colors"
              title="Exportar todos os instrumentos para Excel"
            >
              ↓ Exportar
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="flex-1 text-[11px] py-1.5 rounded-md bg-blue-900/40 text-blue-400 border border-blue-800/50 hover:bg-blue-900/70 transition-colors"
              title="Importar instrumentos — todos serão marcados como calibráveis"
            >
              ↑ Importar
            </button>
            <button
              onClick={() => downloadBlob('/instruments/template', 'template_instrumentos.xlsx')}
              className="flex-1 text-[11px] py-1.5 rounded-md bg-slate-800/60 text-slate-400 border border-slate-700/50 hover:bg-slate-700/60 transition-colors"
              title="Baixar planilha modelo para importação"
            >
              ↓ Template
            </button>
          </div>

          {/* Limpar + contagem */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-600">
              {filtered.length} de {instruments.length} instrumento{instruments.length !== 1 ? 's' : ''}
            </p>
            {hasActiveFilters && (
              <button
                onClick={() => { setFilterStatus(''); setFilterCriticidade(''); setFilterVencimento(''); setFilterVencimentoDate(''); setFilterSector(''); setSearchDesc(''); }}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Limpar tudo
              </button>
            )}
          </div>
        </div>

        {/* Lista de instrumentos */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500 text-sm">
              <span className="text-2xl mb-2">🔍</span>
              <p>Nenhum instrumento encontrado.</p>
            </div>
          ) : (
            filtered.map(inst => {
              const days = getDaysRemaining(inst.nextCalibrationDate);
              let dayClass = 'text-slate-500';
              if (days !== null && days < 0) dayClass = 'text-red-400 font-semibold';
              else if (days !== null && days <= 30) dayClass = 'text-amber-400 font-semibold';
              else if (days !== null && days <= 90) dayClass = 'text-yellow-400';
              const daysText = days === null ? '—' : days < 0 ? `${Math.abs(days)}d atraso` : `${days}d`;

              return (
                <div
                  key={inst._id}
                  onClick={() => setSelected(inst)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selected?._id === inst._id
                      ? 'bg-[#1d4ed8] text-white'
                      : inst.calibrationStatus === 'bloqueado'
                      ? 'hover:bg-[var(--surface-4)] text-[var(--text-base)] border border-red-900/30'
                      : 'hover:bg-[var(--surface-4)] text-[var(--text-base)]'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="min-w-0">
                      <div className="font-mono font-bold text-sm flex items-center gap-1.5">
                        {inst.calibrationStatus === 'bloqueado' && <span className="text-xs">🔒</span>}
                        {inst.tag}
                      </div>
                      <div className="text-xs text-slate-400 truncate max-w-[160px]">{inst.description}</div>
                      {inst.instrumentClass?.name && (
                        <div className="text-[10px] text-slate-600 truncate max-w-[160px]">{inst.instrumentClass.name}</div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <div className={`text-xs ${dayClass}`}>{daysText}</div>
                      <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full mt-1 ${statusClass(inst.calibrationStatus)}`}>
                        {statusLabel(inst.calibrationStatus)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Painel direito — detalhes ───────────────────────────────────────── */}
      {selected ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Back button — mobile only */}
          <div className="lg:hidden flex items-center gap-2 px-4 h-11 border-b border-[var(--surface-4)] bg-[var(--surface-2)] flex-shrink-0">
            <button
              onClick={() => setSelected(null)}
              className="flex items-center gap-1.5 text-blue-400 text-sm hover:text-blue-300 transition-colors"
            >
              ← Voltar
            </button>
            <span className="font-mono font-bold text-sm text-slate-200 ml-2 truncate">{selected.tag}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 lg:p-6">
            <InstrumentDetail
              key={selected._id}
              instrument={selected}
              onUpdate={() => { cacheInvalidate('/instruments'); loadInstruments(true); }}
            />
          </div>
        </div>
      ) : (
        <div className="hidden lg:flex flex-1 flex-col items-center justify-center text-slate-500">
          <span className="text-4xl mb-3">📋</span>
          <p className="text-sm">Selecione um instrumento à esquerda</p>
        </div>
      )}

      <ImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        forceCalibratable={true}
        onImportComplete={() => {
          cacheInvalidate('/instruments?calibratable=true');
          loadInstruments(true);
        }}
      />
    </div>
  );
}
